import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getAnalyticsManagers } from '../../../shared/api/clientsAnalytics.api'
import { usePlanningSelection } from '../../planning/usePlanningSelection'

const COLLAPSED_KEY = 'manager-chips.collapsed'

function readCollapsed(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Чипы выбора менеджеров для виджета аналитики на дашборде.
 *
 * Селекция — общая с таблицами /planning (через users.gridSettings.planningSelection).
 * Клик по чипу = toggle менеджера в общем selection. Чип «Все менеджеры»:
 *   - если выбраны ВСЕ — снимает выбор (selection = пусто);
 *   - иначе — выбирает всех.
 *
 * Список менеджеров приходит с API (для viewer=ROP применяется city-filter).
 *
 * Блок сворачиваемый: клик по заголовку прячет чипы, состояние хранится в
 * localStorage чтобы не разворачивать заново при каждой загрузке дашборда.
 */
export function ManagerChips() {
  const { data: managers, isLoading } = useQuery({
    queryKey: ['analytics-managers'],
    queryFn: getAnalyticsManagers,
    staleTime: 60_000,
  })
  const { selection, toggle, setAll, clear } = usePlanningSelection()
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed)

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0')
    } catch {
      /* localStorage недоступен — игнорируем, состояние просто не сохранится */
    }
  }, [collapsed])

  if (isLoading || !managers) return null
  if (managers.length === 0) {
    return (
      <div className="text-xs text-gray-400">
        Нет доступных менеджеров. Создайте сотрудников с ролью MANAGER/ROP в{' '}
        <Link to="/accounts" className="text-brand-600 hover:underline">
          Аккаунтах
        </Link>
        .
      </div>
    )
  }

  const allSelected = managers.every((m) => selection.has(m.id))
  const selectedCount = managers.filter((m) => selection.has(m.id)).length

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-400 hover:text-gray-600 transition-colors select-none w-fit"
        title={collapsed ? 'Развернуть список' : 'Свернуть список'}
      >
        <span className="inline-block w-3 text-center">{collapsed ? '▶' : '▼'}</span>
        <span>Выбор менеджера</span>
        {collapsed && (
          <span className="normal-case tracking-normal text-gray-500">
            — выбрано {selectedCount} из {managers.length}
          </span>
        )}
      </button>
      {!collapsed && (
        <div className="flex items-center gap-2 flex-wrap">
          <Chip
            on={allSelected}
            onClick={() => (allSelected ? clear() : setAll(managers.map((m) => m.id)))}
            label="Все менеджеры"
          />
          {managers.map((m) => (
            <Chip
              key={m.id}
              on={selection.has(m.id)}
              onClick={() => toggle(m.id)}
              label={m.name || m.email}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function Chip({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-colors ${
        on
          ? 'bg-brand-50 border-brand-300 text-brand-700'
          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
      }`}
    >
      <span
        className={`w-3.5 h-3.5 rounded-sm flex items-center justify-center text-[10px] ${
          on
            ? 'bg-emerald-500 text-white border border-emerald-500'
            : 'bg-white border border-gray-300'
        }`}
      >
        {on ? '✓' : ''}
      </span>
      {label}
    </button>
  )
}
